import SelectField from '../shared/SelectField';
import { ANY_GENRE, getLibraryGenres, filterLibraryItems } from '../../utils/libraryFilters';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Film, Tv, Gamepad2, Music, BookOpen, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useShelf } from '../../context/ShelfContext';
import { useShelfFilters } from '../../context/ShelfFilterContext';

const PanelContainer = styled.aside`
  width: 320px;
  background: transparent;
  border-left: 0px solid rgba(255, 255, 255, 0.08);
  height: calc(100vh - 64px);
  position: fixed;
  right: var(--vk-offset, 0px);
  top: 64px;
  padding: 20px;
  overflow-y: auto;
  z-index: 100;
  color: ${({ theme }) => theme.colors.text};
  transition: background-color 0.3s ease, border-color 0.3s ease;

  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 1200px) { width: 280px; }
  @media (max-width: 1024px) { display: none; }
`;

const Section = styled.section`
  margin-bottom: 24px;
  padding: 20px;
  border-radius: 20px;
  background: ${({ theme }) => theme.isDarkMode ? '#18191B' : '#EFF3F7'};
  transition: background-color 0.3s ease;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
`;

const SectionSubtitle = styled.h4`
  font-size: 13px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.72)' : '#18191B'};
  margin-bottom: 12px;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
  line-height: 1;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.72)' : '#18191B'};
  font-size: 13px;
`;

const ProgressRow = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 16px;
`;

const ProgressItem = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
`;

const ProgressLabel = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.72)' : '#18191B'};
  font-size: 13px;
  min-width: 28px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ProgressBar = styled.div`
  height: 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(24, 25, 27, 0.08)'};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${({ theme }) => theme.isDarkMode ? '#EFF3F7' : '#18191B'};
`;

const GenreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GenreItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(24, 25, 27, 0.04)'};
`;

const GenreName = styled.span`
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
  font-size: 13px;
  font-weight: 500;
`;

const GenreCount = styled.span`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#18191B'};
  font-size: 12px;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(24, 25, 27, 0.12)'};
  padding: 2px 8px;
  border-radius: 6px;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border-radius: 12px;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(24, 25, 27, 0.04)'};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(24, 25, 27, 0.08)'};
  }
`;

const ActivityCover = styled.img`
  width: 48px;
  height: 68px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const ActivityInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActivityTitle = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#18191B'};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 18ch;
`;

const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActivityCategory = styled.span`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#18191B'};
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(24, 25, 27, 0.12)'};
`;

const ActivityDate = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#18191B'};
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 0;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#18191B'};
  font-size: 13px;
`;

const FilterPillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const FilterPill = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  background: ${props => props.$active ? '#18191B' : '#D9D9D9'};
  color: ${props => props.$active ? 'white' : '#101113'};
  &:hover { opacity: 0.9; }
`;


const YearInputContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const YearInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.isDarkMode ? '#101113' : '#FBFBFB'};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  &::placeholder { color: ${({ theme }) => theme.colors.textSecondary}; }
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
`;

const ResetButton = styled.button`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: none;
  background: #D9D9D9;
  color: #101113;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 8px;
  &:hover { background: #C9C9C9; }
`;

const getCategoryIcon = (category) => {
  switch (category) {
    case 'film': return <Film size={12} />;
    case 'series': return <Tv size={12} />;
    case 'game': return <Gamepad2 size={12} />;
    case 'music': return <Music size={12} />;
    case 'book': return <BookOpen size={12} />;
    default: return null;
  }
};

const ShelfRightPanel = () => {
  const { t } = useTranslation();
  const { shelfItems } = useShelf();
  const location = useLocation();

  const {
    activeContentType, setActiveContentType,
    activeStatus,
    genre, setGenre,
    yearFrom, setYearFrom,
    yearTo, setYearTo,
    resetFilters
  } = useShelfFilters();

  const stats = useMemo(() => {
    const total = shelfItems.length;
    const films = shelfItems.filter(i => i.category === 'film').length;
    const series = shelfItems.filter(i => i.category === 'series').length;
    const games = shelfItems.filter(i => i.category === 'game').length;
    const music = shelfItems.filter(i => i.category === 'music').length;
    const books = shelfItems.filter(i => i.category === 'book').length;
    return { total, films, series, games, music, books };
  }, [shelfItems]);

  const genres = useMemo(() => {
    const genreCount = {};
    shelfItems.forEach(item => {
      const itemGenres = getLibraryGenres(item.genres);
      itemGenres.forEach(genreName => {
        if (genreName && genreName.trim()) {
          genreCount[genreName.trim()] = (genreCount[genreName.trim()] || 0) + 1;
        }
      });
    });
    if (Object.keys(genreCount).length === 0) return [];
    return Object.entries(genreCount).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, count]) => ({ name, count }));
  }, [shelfItems]);

  const uniqueGenres = useMemo(() => {
    const genreSet = new Set();
    shelfItems.forEach(item => {
      const itemGenres = getLibraryGenres(item.genres);
      itemGenres.forEach(g => genreSet.add(g));
    });
    return Array.from(genreSet).sort();
  }, [shelfItems]);

  const recentActivity = useMemo(() => {
    return [...shelfItems].sort((a, b) => new Date(b.added_at || b.addedAt || 0) - new Date(a.added_at || a.addedAt || 0)).slice(0, 5);
  }, [shelfItems]);

  const filteredItems = useMemo(() => filterLibraryItems(shelfItems, {
    activeContentType, activeStatus, genre, yearFrom, yearTo,
  }), [shelfItems, activeContentType, activeStatus, genre, yearFrom, yearTo]);

  if (!location.pathname.startsWith('/shelf')) return null;

  const getCategoryName = (category) => {
    return t(`itemDetail.category.${category}`, '');
  };

  const getItemImage = (item) => {
    if (item.category === 'music') return item.artwork || item.cover || item.poster || item.image || 'https://via.placeholder.com/48x68?text=Music';
    if (item.category === 'game') return item.poster || item.cover || item.image || 'https://via.placeholder.com/48x68?text=Game';
    return item.poster || item.cover || item.image || 'https://via.placeholder.com/48x68?text=Item';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('common.unknown');
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return t('common.unknown');
    return d.toLocaleDateString();
  };

  return (
    <PanelContainer>
      <Section>
        <SectionHeader><SectionTitle>{t('rightPanel.stats')}</SectionTitle></SectionHeader>
        <StatValue>{stats.total}</StatValue>
        <StatLabel>{t('rightPanel.totalProjects')}</StatLabel>
        <ProgressRow>
          <ProgressItem>
            <ProgressLabel>{getCategoryIcon('film')}{t('rightPanel.films')}</ProgressLabel>
            <ProgressBar><ProgressFill style={{ width: `${stats.total ? Math.round((stats.films / stats.total) * 100) : 0}%` }} /></ProgressBar>
            <StatLabel>{stats.films}</StatLabel>
          </ProgressItem>
          <ProgressItem>
            <ProgressLabel>{getCategoryIcon('series')}{t('rightPanel.series')}</ProgressLabel>
            <ProgressBar><ProgressFill style={{ width: `${stats.total ? Math.round((stats.series / stats.total) * 100) : 0}%` }} /></ProgressBar>
            <StatLabel>{stats.series}</StatLabel>
          </ProgressItem>
          <ProgressItem>
            <ProgressLabel>{getCategoryIcon('game')}{t('rightPanel.games')}</ProgressLabel>
            <ProgressBar><ProgressFill style={{ width: `${stats.total ? Math.round((stats.games / stats.total) * 100) : 0}%` }} /></ProgressBar>
            <StatLabel>{stats.games}</StatLabel>
          </ProgressItem>
          <ProgressItem>
            <ProgressLabel>{getCategoryIcon('music')}{t('rightPanel.music')}</ProgressLabel>
            <ProgressBar><ProgressFill style={{ width: `${stats.total ? Math.round((stats.music / stats.total) * 100) : 0}%` }} /></ProgressBar>
            <StatLabel>{stats.music}</StatLabel>
          </ProgressItem>
          <ProgressItem>
            <ProgressLabel>{getCategoryIcon('book')}{t('rightPanel.books')}</ProgressLabel>
            <ProgressBar><ProgressFill style={{ width: `${stats.total ? Math.round((stats.books / stats.total) * 100) : 0}%` }} /></ProgressBar>
            <StatLabel>{stats.books}</StatLabel>
          </ProgressItem>
        </ProgressRow>
      </Section>

      <Section>
        <SectionHeader><SectionTitle>{t('rightPanel.filters')}</SectionTitle></SectionHeader>
        <SectionSubtitle>{t('rightPanel.contentType')}</SectionSubtitle>
        <FilterPillContainer>
          <FilterPill $active={activeContentType === 'all'} onClick={() => setActiveContentType('all')}>{t('rightPanel.all')}</FilterPill>
          <FilterPill $active={activeContentType === 'film'} onClick={() => setActiveContentType('film')}>{t('rightPanel.films')}</FilterPill>
          <FilterPill $active={activeContentType === 'series'} onClick={() => setActiveContentType('series')}>{t('rightPanel.series')}</FilterPill>
          <FilterPill $active={activeContentType === 'game'} onClick={() => setActiveContentType('game')}>{t('rightPanel.games')}</FilterPill>
          <FilterPill $active={activeContentType === 'music'} onClick={() => setActiveContentType('music')}>{t('rightPanel.music')}</FilterPill>
          <FilterPill $active={activeContentType === 'book'} onClick={() => setActiveContentType('book')}>{t('rightPanel.books')}</FilterPill>
        </FilterPillContainer>
        <SelectField label={t('rightPanel.genre')} value={genre} onValueChange={setGenre}
          options={[{ value: ANY_GENRE, label: t('rightPanel.anyGenre') }, ...uniqueGenres.map(value => ({ value, label: value }))]} />
        <SectionSubtitle>{t('rightPanel.year')}</SectionSubtitle>
        <YearInputContainer>
          <YearInput type="number" placeholder={t('rightPanel.yearFrom')} value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} min="1900" max="2100" />
          <YearInput type="number" placeholder={t('rightPanel.yearTo')} value={yearTo} onChange={(e) => setYearTo(e.target.value)} min="1900" max="2100" />
        </YearInputContainer>
        <ResetButton onClick={resetFilters}>
          {filteredItems.length !== shelfItems.length ? `${t('rightPanel.resetFilters')} (${filteredItems.length})` : t('rightPanel.resetFilters')}
        </ResetButton>
      </Section>

      <Section>
        <SectionHeader><SectionTitle>{t('rightPanel.genres')}</SectionTitle></SectionHeader>
        {genres.length > 0 ? (
          <GenreList>{genres.map((genre, index) => (<GenreItem key={index}><GenreName>{genre.name}</GenreName><GenreCount>{genre.count}</GenreCount></GenreItem>))}</GenreList>
        ) : (<EmptyState>{t('rightPanel.noGenres')}</EmptyState>)}
      </Section>

      <Section>
        <SectionHeader><SectionTitle>{t('rightPanel.recentActivity')}</SectionTitle></SectionHeader>
        {recentActivity.length > 0 ? (
          <ActivityList>
            {recentActivity.map((item, index) => (
              <ActivityItem key={index}>
                <ActivityCover src={getItemImage(item)} alt={item.title} />
                <ActivityInfo>
                  <ActivityTitle>{item.title}</ActivityTitle>
                  <ActivityMeta>
                    <ActivityCategory>{getCategoryName(item.category)}</ActivityCategory>
                    <ActivityDate><Clock size={12} />{formatDate(item.added_at || item.addedAt)}</ActivityDate>
                  </ActivityMeta>
                </ActivityInfo>
              </ActivityItem>
            ))}
          </ActivityList>
        ) : (<EmptyState>{t('rightPanel.noRecentActivity')}</EmptyState>)}
      </Section>
    </PanelContainer>
  );
};

export default ShelfRightPanel;
